import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, EnvelopeSimple, LockSimple } from '@phosphor-icons/react';
import { isApiClientError, type PlatformChallenge } from '@sm/api';
import { loginSchema } from '@sm/core';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { PlatformLoginPage } from '@/features/platform/PlatformLoginPage';
import { api, unwrap } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { syncFromServer } from '@/lib/preferences';
import { qk } from '@/lib/queries';
import { loginRoute } from '@/router';

import { AuthAlert, AuthHeading, FieldError, PasswordInput } from './AuthLayout';
import { OidcButtons } from './OidcButtons';

const formSchema = loginSchema.pick({ email: true, password: true });
type Form = z.input<typeof formSchema>;

/** Dozvoljava samo relativne putanje unutar panela (zaštita od open redirecta). */
export function safeRedirect(r: string | undefined): string {
  if (!r || !r.startsWith('/') || r.startsWith('//') || r.startsWith('/prijava')) return '/';
  return r;
}

export function LoginPage() {
  const [challenge, setChallenge] = useState<PlatformChallenge | null>(null);
  return challenge ? (
    <PlatformLoginPage embedded initialChallenge={challenge} onRestart={() => setChallenge(null)} />
  ) : (
    <CompanyLoginPage onChallenge={setChallenge} />
  );
}

function CompanyLoginPage({ onChallenge }: { onChallenge: (value: PlatformChallenge) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = loginRoute.useSearch();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    try {
      const res = await unwrap(
        api.POST('/auth/login', { body: { email: v.email, password: v.password, client: 'web' } }),
      );
      if ('platformChallenge' in res) {
        onChallenge(res.platformChallenge);
        return;
      }
      qc.setQueryData(qk.me, res.me);
      syncFromServer(res.me);
      await navigate({ href: safeRedirect(search.redirect) });
    } catch (e) {
      // Ista poruka za svaki neuspjeh prijave (bez enumeracije naloga).
      if (isApiClientError(e) && (e.status === 401 || e.status === 400))
        setError(t('auth:login.invalidCredentials'));
      else if (isApiClientError(e)) setError(t(e.i18nKey));
      else setError(t('errors:network_error'));
    }
  });

  return (
    <>
      <AuthHeading title={t('auth:login.title')} subtitle={t('auth:login.subtitle')} />
      {search.reason === 'expired' ? <AuthAlert>{t('auth:login.sessionExpired')}</AuthAlert> : null}
      {search.reason === 'logout' ? (
        <AuthAlert tone="ok">{t('auth:login.loggedOut')}</AuthAlert>
      ) : null}
      {search.reason === 'deleted' || search.reason === 'deleted_company' ? (
        <div data-testid="deletion-requested">
          <AuthAlert tone="ok">
            {t(
              search.reason === 'deleted_company'
                ? 'auth:login.deletionRequestedCompany'
                : 'auth:login.deletionRequested',
              { date: fmtDate(search.until) },
            )}
          </AuthAlert>
        </div>
      ) : null}
      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        className="flex flex-col gap-4"
        data-testid="login-form"
      >
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
          {t('auth:login.email')}
          <span
            className={`flex min-h-[46px] items-center gap-2.5 rounded-md border bg-surface px-3.5 text-muted focus-within:border-accent focus-within:shadow-[0_0_0_3px_var(--chip)] ${errors.email ? 'border-danger' : 'border-line2'}`}
          >
            <EnvelopeSimple size={18} />
            <input
              type="email"
              autoComplete="username"
              placeholder={t('auth:login.emailPlaceholder')}
              aria-invalid={!!errors.email || undefined}
              data-testid="login-email"
              className="min-w-0 flex-1 border-0 bg-transparent text-[15px] font-normal text-text outline-none"
              {...register('email')}
            />
          </span>
          <FieldError msg={errors.email?.message} />
        </label>
        <div className="flex flex-col gap-1.5 text-[13px] font-semibold">
          <span className="flex justify-between gap-2.5">
            <label htmlFor="login-password">{t('auth:login.password')}</label>
            <Link to="/zaboravljena-lozinka" className="text-[13px] font-semibold text-accent">
              {t('auth:login.forgot')}
            </Link>
          </span>
          <PasswordInput
            id="login-password"
            autoComplete="current-password"
            placeholder="••••••••"
            icon={<LockSimple size={18} />}
            invalid={!!errors.password}
            data-testid="login-password"
            {...register('password')}
          />
          <FieldError msg={errors.password?.message} />
        </div>
        {error ? <AuthAlert>{error}</AuthAlert> : null}
        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="login-submit"
          className="mt-1 flex min-h-12 items-center justify-center gap-2 rounded-md border-0 bg-btn text-[15px] font-semibold text-btn-fg hover:bg-btn-h disabled:opacity-70"
        >
          {isSubmitting ? t('auth:login.submitting') : t('auth:login.submit')}
          <ArrowRight size={17} />
        </button>
      </form>
      <OidcButtons
        intent="login"
        {...(search.redirect ? { returnTo: safeRedirect(search.redirect) } : {})}
      />
      <Link
        to="/registracija"
        data-testid="login-register"
        className="flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-md border border-accent bg-chip px-4 py-2.5 text-center text-accent no-underline hover:bg-surface"
      >
        <span className="text-[15px] font-semibold">
          {t('auth:login.noAccount')} {t('auth:login.registerLink')}
        </span>
        <span className="text-xs font-medium text-muted">{t('auth:login.registerCta')}</span>
      </Link>
    </>
  );
}
